import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * The design has two stat shapes and they are not interchangeable:
 *
 *   StatCard   the clickable crew filter card (`crewStats` / `statStyle`,
 *              line 1613) — dot + count + caption, selectable
 *   GlanceStat the read-only cell inside "Site at a glance" (markup line 108)
 *              — icon + number + caption, split by a 1px rule
 */

/** Semantic of a crew filter card — mirrors the three `crewStats` keys. */
export type StatTone = "ok" | "warn" | "danger";

/**
 * Selection swaps the 1.5px neutral outline for a *2px* one in the semantic
 * colour and tints the surface with that semantic's pill background. The
 * half-pixel jump is the source's own behaviour, not a rounding slip.
 */
const TONES: Record<StatTone, { dot: string; selected: string }> = {
  ok: { dot: "bg-ok", selected: "border-ok bg-ok-bg" },
  warn: { dot: "bg-warn", selected: "border-warn bg-warn-bg" },
  danger: { dot: "bg-danger", selected: "border-danger bg-danger-bg" },
};

export type StatCardProps = {
  tone: StatTone;
  count: number;
  /** "Valid" / "Expiring < 30d" / "Missing / expired". */
  label: string;
  selected?: boolean;
  onClick?: () => void;
  className?: string;
};

export function StatCard({
  tone,
  count,
  label,
  selected = false,
  onClick,
  className,
}: StatCardProps) {
  const t = TONES[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "flex flex-1 cursor-pointer flex-col gap-[5px] rounded-card",
        "p-[16px_18px] text-left",
        selected
          ? cn("border-2", t.selected)
          : "border-[1.5px] border-border bg-surface",
        className,
      )}
    >
      <span className="flex items-center gap-2">
        <span className={cn("size-[10px] shrink-0 rounded-full", t.dot)} />
        <span className="font-display text-[2rem] font-bold text-ink">
          {count}
        </span>
      </span>
      <span className="text-[0.84rem] font-semibold text-muted">{label}</span>
    </button>
  );
}

export type GlanceStatProps = {
  icon: LucideIcon;
  value: number | string;
  label: string;
  /**
   * Text-colour utility for the icon only — the number is always `text-ink`.
   * "On site today" is `text-brand`; "Open flags" is `text-danger` when
   * anything is missing/expired and `text-warn` otherwise.
   */
  tone?: string;
  className?: string;
};

export function GlanceStat({
  icon: Icon,
  value,
  label,
  tone = "text-brand",
  className,
}: GlanceStatProps) {
  return (
    <div className={cn("flex-1", className)}>
      <div className={cn("flex items-center gap-[7px]", tone)}>
        <Icon size={18} className="shrink-0" />
        <span className="font-display text-[2rem] font-bold text-ink">
          {value}
        </span>
      </div>
      <div className="mt-0.5 text-[0.82rem] font-semibold text-muted">
        {label}
      </div>
    </div>
  );
}
